/**
 * Página `/finanzas-aseador-admin` — vista admin/superadmin del módulo
 * "Finanzas del aseador". Permite:
 *
 *   1. **Por aseador**: seleccionar un aseador y ver exactamente lo mismo
 *      que ese aseador ve en su propio panel `/finanzas-aseador`
 *      (mismos datos, misma fórmula, mismos estados). Útil para responder
 *      preguntas del trabajador, validar pagos antes del lunes y entender
 *      el detalle.
 *   2. **Global**: tabla con todos los aseadores activos, totales del rango
 *      seleccionado, # de aseos, ya pagado vs pendiente. Click en una fila
 *      → abre la vista por-aseador con ese aseador seleccionado.
 *
 * Backend: `GET /api/finanzas/aseador/:id` (admin-only). Reusa
 * `buildResumenFinanzasAseador()` del controlador → paridad numérica
 * garantizada con el panel del propio aseador. Ver
 * `docs/modulo-finanzas-aseador.md`.
 *
 * Por qué no hay endpoint "global" dedicado todavía: en Fase 1 hacemos N
 * llamadas en paralelo (`Promise.allSettled`) — costo aceptable mientras
 * #aseadores < ~30. Si crece, ver Fase 2 (TODO: endpoint
 * `/api/finanzas/aseadores/global`).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckSquareOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { UserAvatar } from "../../Components/UserAvatar";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useSidebar } from "../../hooks/useSidebar";
import { useWebSocket } from "../../context/WebSocketContext";
import { finanzasApi, usersApi } from "../../services/api";
import type {
  MesFinanzaAseador,
  MovimientoAseador,
  MovimientoEstado,
} from "../../services/finanzas.api";
import type { Cleaner } from "../../interfaces/api.interfaces";
import FinanzasAseadorView from "../finanzas-aseador/FinanzasAseadorView";
import RegistrarMovimientoModal from "./RegistrarMovimientoModal";
import { fuzzyIncludes } from "../../utils/fuzzyMatch";
import "../finanzas-aseador/finanzasAseador.css";
import "./finanzasAseadorAdmin.css";
import { ahoraChile } from "../../utils/fechaChile";
import { cambiarEstadoAsignacionLavanderia } from "../../services/finanzasLavanderia.api";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import {
  CADUCIDAD_FECHAS_DIAS,
  deserializarRango,
  serializarRango,
} from "../../hooks/filtrosPersistidos";

import { paginacionCliente } from "../../utils/tablaPaginacion";
// `isoWeek` da semana ISO 8601 (lunes = inicio). Coincide con el cron del
// backend (`date_trunc('week', current_date)` en `aprobar-pendientes-bulk`
// y `aseadorPagosCron`). DEBE ir DESPUÉS de los imports (regla
// `import/first` de CRA-eslint, lección round 2026-04-29). Si lo
// extendemos en otro componente más adelante, dayjs ignora la doble
// extensión, así que es seguro.
dayjs.extend(isoWeek);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const fmtClp = (n: number): string =>
  `$${Math.round(n).toLocaleString("es-CL")}`;

// Valor centinela de UI para el caso "Sin aseador designado" (aseos con
// id_usuario IS NULL). NO es un id real: al detectarlo, el frontend pega al
// endpoint dedicado `/finanzas/aseador/sin-asignar` (sin :id) y renderiza la
// vista en modo SOLO LECTURA (sin acciones de pago/aprobación — pagar un aseo
// sin aseador sería "pago fantasma"). Round 2026-07-12.
const SIN_ASEADOR = -1;

/**
 * Fila resumida por aseador para la tabla "Global". Se calcula sumando los
 * meses devueltos por el endpoint `/api/finanzas/aseador/:id`.
 */
interface ResumenAseador {
  aseadorId: number;
  nombre: string;
  cantidadAseos: number;
  totalNeto: number;
  totalPagado: number;
  totalPendiente: number;
  totalSinEstado?: number;
  /** Semilla Multiavatar del aseador (columna `avatar`). Para la carita. */
  avatar?: string | null;
  /** Foto subida del aseador (URL firmada). */
  avatarUrl?: string | null;
  /** `true` si la llamada falló — la fila se pinta atenuada con tooltip de error. */
  error?: boolean;
  /** `true` para la fila especial "Sin aseador designado" (read-only, sin pago). */
  sinAseador?: boolean;
}

// Round 2026-04-30 quinquies — banner re-escrito para reflejar el ledger
// completo (aseos + movimientos extra). La descripción anterior solo
// hablaba de aseos y de la fórmula `neto = sueldo + bono − descuento`,
// lo que confundía cuando había gastos pagados al aseador desde
// /finanzas-empresa que no encajaban en esa fórmula.
const ModuleInfoBanner: React.FC = () => (
  <Alert
    type="info"
    showIcon
    icon={<InfoCircleOutlined />}
    closable
    style={{ marginBottom: 12 }}
    message="Finanzas del aseador — vista admin"
    description={
      <div style={{ fontSize: 13, lineHeight: 1.55 }}>
        El ledger del aseador combina <strong>dos fuentes</strong> de datos
        — todo lo que vincula plata con el aseador entra automáticamente:

        <div style={{ marginTop: 8 }}>
          <b>1. Aseos {(<Tag style={{ marginInline: 4 }} color="purple">tabla cleanings</Tag>)}</b>{" "}
          — uno por aseo asignado al aseador (`cleanings.id_usuario =
          aseador`). Por aseo:{" "}
          <code>neto = sueldo_aseador</code>. Estados:
          <Tag style={{ marginInline: 4 }} color="default">Agendado</Tag>
          <Tag style={{ marginInline: 4 }} color="blue">En proceso</Tag>
          <Tag style={{ marginInline: 4 }} color="gold">Completado (por pagar)</Tag>
          <Tag style={{ marginInline: 4 }} color="green">Pagado</Tag>.
          Los completados entran al pago semanal del lunes (Lun-Dom de la
          semana anterior). Acciones inline por aseo: "Marcar pagado" /
          "Anular pago". El bulk semanal histórico vive en{" "}
          <a href="/aseos">/aseos</a>.
        </div>

        <div style={{ marginTop: 8 }}>
          <b>2. Movimientos extra {(<Tag style={{ marginInline: 4 }} color="purple">tabla gastos</Tag>)}</b>{" "}
          — cualquier <code>gasto</code> donde el aseador aparece en{" "}
          <code>pagado_a</code> (ingreso, signo +) o <code>pagado_por</code>{" "}
          (egreso, signo −). Cubre adelantos, bonos extra, descuentos
          disciplinarios, multas, reembolsos de gastos, propinas. Estados:
          <Tag style={{ marginInline: 4 }} color="gold">Pendiente</Tag>
          <Tag style={{ marginInline: 4 }} color="green">Aprobado</Tag>
          <Tag style={{ marginInline: 4 }} color="red">Denegado</Tag>.
          Acciones por fila: Aprobar / Denegar (si pendiente), Anular pago
          / Editar (si pagado o denegado — la edición vuelve a pendiente
          para forzar re-aprobación). Los gastos creados en{" "}
          <code>/finanzas-empresa</code> con destinatario aseador{" "}
          <strong>aparecen aquí automáticamente</strong>; no hay doble
          registro.
        </div>

        <div style={{ marginTop: 8 }}>
          <b>Total general (neto)</b> ={" "}
          <code>Σ aseos.neto + Σ movimientos.signo·monto</code>. Los
          denegados NO suman; quedan visibles como auditoría.
        </div>

        <div style={{ marginTop: 8 }}>
          <b>Sobre la utilidad AF</b>: pagar al aseador por aseos es rol
          AF <Tag color="purple">intermediario</Tag> (huésped paga, AF
          transfiere) — <u>no afecta utilidad</u>. Movimientos extra con
          contraparte=AF (adelantos, bonos) sí entran a la utilidad
          (egreso o ingreso de empresa).
        </div>

        Ver{" "}
        <a
          href="https://github.com/"
          onClick={(e) => e.preventDefault()}
          title="docs/flujo-financiero.md §6 + docs/modulo-finanzas-aseador.md §3"
        >
          docs/modulo-finanzas-aseador.md §3
        </a>{" "}
        para el inventario completo de fuentes.
      </div>
    }
  />
);

export default function FinanzasAseadorAdmin() {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { socket } = useWebSocket();

  // Round 2026-04-30 quater — default 'global'. Antes era 'por-aseador',
  // pero al entrar al panel sin un aseador seleccionado salía todo vacío
  // y el admin no veía nada de información agregada. La vista global
  // siempre tiene contenido (lista de todos los aseadores y sus totales),
  // y un click en una fila lleva al detalle "Por aseador" con drill-down.
  // Round 2026-09-24: los filtros del toolbar se recuerdan por usuario. El
  // default "global" sigue valiendo para quien entra por primera vez.
  const [vista, setVista] = useFiltroPersistido<"por-aseador" | "global">(
    "finanzasAseadorAdmin.vista",
    "global",
    { validar: (v) => v === "por-aseador" || v === "global" },
  );
  const [months, setMonths] = useFiltroPersistido<number>(
    "finanzasAseadorAdmin.meses",
    6,
    { validar: (v) => v === 3 || v === 6 || v === 12 },
  );

  // ─── Modo de rango: semanas (default) o meses ────────────────────────
  // Round 2026-04-30 ter — el default pasa a ser SEMANAL porque el flujo
  // de pagos del aseador es semanal (cron del lunes paga la semana
  // anterior); el admin abre este panel principalmente para revisar la
  // semana en curso o aprobar la semana pasada. En modo `semana` cada
  // panel del Collapse es UNA semana laboral (Lun-Dom ISO 8601); en modo
  // `meses` cada panel es un mes calendario.
  //
  // `weeks=4` por default: muestra 4 semanas terminando en la semana
  // actual (la última en el array, que después del `.reverse()` queda
  // PRIMERA y abierta por defecto en el Collapse — ver `defaultActiveKey`
  // en `FinanzasAseadorView`). Eso satisface "cargar siempre en la semana
  // actual, no en la anterior u otra".
  const [rangeMode, setRangeMode] = useFiltroPersistido<"meses" | "semana">(
    "finanzasAseadorAdmin.modoRango",
    "semana",
    { validar: (v) => v === "meses" || v === "semana" },
  );
  const [weeks, setWeeks] = useFiltroPersistido<number>(
    "finanzasAseadorAdmin.semanas",
    4,
    { validar: (v) => v === 4 || v === 8 || v === 12 },
  );
  // Rango de fechas puntual para el modo MESES (round 2026-07-12). Si está
  // seteado, override del segmented 3/6/12: pide ese rango como UN solo bucket
  // (backend modo custom `?from&to`), igual que el picker de semana en modo
  // semanal. `null` = usar el segmented de N meses.
  // Se recuerda, pero caduca a los 7 días: este panel aprueba y paga, y un
  // rango viejo restaurado cambia en silencio qué se aprueba en bloque.
  const [rangoMeses, setRangoMeses] = useFiltroPersistido<
    [dayjs.Dayjs, dayjs.Dayjs] | null
  >("finanzasAseadorAdmin.rangoMeses", null, {
    caducaEnDias: CADUCIDAD_FECHAS_DIAS,
    serializar: serializarRango,
    deserializar: (crudo) => {
      const rango = deserializarRango(crudo);
      if (rango === undefined) return undefined;
      // El picker es de rango completo: media fecha no sirve, vale como vacío.
      if (!rango || !rango[0] || !rango[1]) return null;
      return [rango[0], rango[1]] as [dayjs.Dayjs, dayjs.Dayjs];
    },
  });
  // DatePicker week. Round 2026-04-30 quinquies — por defecto inicializa
  // a `ahoraChile()` (semana ISO actual). Antes era null y el segmented "4/8/12
  // sem." quedaba activo, mostrando 4 semanas con la actual al final. El
  // usuario quiere ver SOLO la semana actual al entrar; el segmented N
  // semanas pasa a opt-in (clear el DatePicker para ver más historial).
  // Por eso mismo NO se recuerda entre visitas (round 2026-09-24): restaurar
  // una semana vieja contradice ese "siempre la semana actual al entrar" y
  // dejaría el botón "Aprobar pendientes del rango visible" operando sobre
  // una semana que el admin no eligió hoy.
  const [semana, setSemana] = useState<dayjs.Dayjs | null>(ahoraChile());

  // ─── Lista de aseadores (compartida por ambas vistas) ─────────────────
  const [aseadores, setAseadores] = useState<Cleaner[]>([]);
  const [loadingAseadores, setLoadingAseadores] = useState(true);

  // ─── Vista por-aseador ────────────────────────────────────────────────
  // El aseador elegido se recuerda. SIN_ASEADOR (-1) es un valor real del
  // Select (aseos sin asignar), no un id de usuario: también vale.
  const [aseadorId, setAseadorId] = useFiltroPersistido<number | null>(
    "finanzasAseadorAdmin.aseador",
    null,
    { validar: (v) => v === null || (typeof v === "number" && v !== 0) },
  );
  const [meses, setMeses] = useState<MesFinanzaAseador[]>([]);
  const [aseadorNombre, setAseadorNombre] = useState<string>("");
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState<string | null>(null);

  // ─── Vista global ────────────────────────────────────────────────────
  const [resumenes, setResumenes] = useState<ResumenAseador[]>([]);
  const [loadingGlobal, setLoadingGlobal] = useState(false);
  const [busquedaGlobal, setBusquedaGlobal] = useFiltroPersistido(
    "finanzasAseadorAdmin.busqueda",
    "",
    { validar: (v) => typeof v === "string" },
  );

  // ─── Modal "Registrar movimiento" ────────────────────────────────────
  const [registrarModalOpen, setRegistrarModalOpen] = useState(false);

  // ─── Modal "Editar movimiento" (round 2026-04-30 quinquies) ──────────
  // Cuando es null, el modal está cerrado. Cuando tiene valor, edita ese
  // movimiento. Si el row estaba pagado/denegado, el backend lo regresa
  // a 'pendiente' automáticamente para forzar re-aprobación.
  const [editingMov, setEditingMov] = useState<MovimientoAseador | null>(null);

  // ─── Recordatorio del lunes (Fase 3) ─────────────────────────────────
  // El cron `aseadorPagosCron` (lunes 09:00) dispara el WS
  // `ASEADOR_PAGOS_PENDIENTES_REVISION` con resumen por aseador. Se
  // mantiene en estado mientras la pestaña está abierta — al recargar
  // la página, el aseador puede ver pendientes en el banner del módulo
  // (que viene del fetch normal). Este Alert es la "campanada" inicial.
  const [reminderLunes, setReminderLunes] = useState<{
    from: string;
    to: string;
    aseadores: Array<{
      aseadorId: number;
      nombre: string;
      count: number;
      monto: number;
    }>;
  } | null>(null);
  // Spinner cuando estamos ejecutando la aprobación bulk.
  const [bulkLoading, setBulkLoading] = useState(false);

  // Cargar lista de aseadores al montar.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingAseadores(true);
      const list = await usersApi.getCleaners();
      if (cancelled) return;
      setAseadores(list ?? []);
      setLoadingAseadores(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // El aseador recordado puede haberse dado de baja desde la última visita.
  // Sin esto el Select queda mostrando un número pelado y el detalle carga
  // vacío, que se lee como "este aseador no tiene nada" en vez de "este
  // aseador ya no existe". Solo corre con la lista ya cargada y no vacía
  // (si el fetch falló, `aseadores` queda en [] y no hay nada que comparar).
  useEffect(() => {
    if (loadingAseadores || aseadores.length === 0) return;
    if (aseadorId == null || aseadorId === SIN_ASEADOR) return;
    if (!aseadores.some((a) => a.id === aseadorId)) setAseadorId(null);
  }, [loadingAseadores, aseadores, aseadorId, setAseadorId]);

  /**
   * Convierte el estado actual del rango (modo + selección) en el shape
   * de opts que `getFinanzasAseadorById` entiende. Centralizado en un
   * único lugar para que loadDetalle / refreshSingleResumen / loadGlobal
   * NO dupliquen la lógica de "qué pedir al backend".
   *
   * Precedencia (la primera que matchee gana):
   *  1. Modo "semana" + DatePicker con valor → `{ range: { from, to, label } }`
   *     (un solo bucket — "ir a la semana específica X").
   *  2. Modo "semana" sin DatePicker → `{ weeks: N }` (N week-buckets).
   *  3. Modo "meses" → `{ months: M }` (default).
   */
  const buildRangeOpts = useCallback(() => {
    if (rangeMode === "semana" && semana) {
      const lunes = semana.startOf("isoWeek");
      const domingo = semana.endOf("isoWeek");
      return {
        range: {
          from: lunes.format("YYYY-MM-DD"),
          to: domingo.format("YYYY-MM-DD"),
          label: `Semana ${lunes.format("DD-MM")} → ${domingo.format("DD-MM")}`,
        },
      };
    }
    if (rangeMode === "semana") {
      return { weeks };
    }
    // Modo meses con rango puntual → un solo bucket (custom from/to).
    if (rangeMode === "meses" && rangoMeses) {
      const [desde, hasta] = rangoMeses;
      return {
        range: {
          from: desde.format("YYYY-MM-DD"),
          to: hasta.format("YYYY-MM-DD"),
          label: `${desde.format("DD-MM-YYYY")} → ${hasta.format("DD-MM-YYYY")}`,
        },
      };
    }
    return { months };
  }, [rangeMode, semana, months, weeks, rangoMeses]);

  // Cargar detalle de un aseador específico.
  const loadDetalle = useCallback(
    async (id: number) => {
      setLoadingDetalle(true);
      setErrorDetalle(null);
      // Caso especial "Sin aseador": endpoint dedicado sin :id (read-only).
      const resp =
        id === SIN_ASEADOR
          ? await finanzasApi.getFinanzasAseadorSinAsignar(buildRangeOpts())
          : await finanzasApi.getFinanzasAseadorById(id, buildRangeOpts());
      if (!resp) {
        setErrorDetalle(
          "No pude cargar las finanzas de este aseador. Intenta de nuevo.",
        );
        setMeses([]);
        setAseadorNombre("");
      } else {
        // Mostrar más reciente arriba (mismo orden que el panel del aseador).
        setMeses([...resp.data.meses].reverse());
        setAseadorNombre(resp.data.aseador.nombre);
      }
      setLoadingDetalle(false);
    },
    [buildRangeOpts],
  );

  // Re-fetch cuando cambia el aseador o cualquier dimensión del rango.
  useEffect(() => {
    if (aseadorId) void loadDetalle(aseadorId);
  }, [aseadorId, rangeMode, months, weeks, semana, rangoMeses, loadDetalle]);

  /**
   * Recalcula la fila resumen de un aseador específico vía un solo fetch.
   * Usada por el handler WS para mantener fresca la vista global sin re-
   * fetchear todas las N filas. Si el aseador no estaba en la tabla
   * (alta nueva), lo agrega; si ya estaba, lo reemplaza in-place.
   */
  const refreshSingleResumen = useCallback(
    async (id: number) => {
      const resp = await finanzasApi.getFinanzasAseadorById(id, buildRangeOpts());
      if (!resp) return;
      // Ledger COMPLETO (aseos + movimientos), igual que loadGlobal — ver nota
      // allí. (Audit 2026-06-21.)
      const tot = resp.data.meses.reduce(
        (acc, mes) => ({
          cantidadAseos: acc.cantidadAseos + mes.cantidadAseos,
          totalNeto: acc.totalNeto + mes.totalGeneralNeto,
          totalPagado: acc.totalPagado + mes.totalGeneralPagado,
          totalPendiente: acc.totalPendiente + mes.totalGeneralPendiente,
          totalSinEstado: acc.totalSinEstado + (mes.totalMovimientosSinEstado || 0),
        }),
        { cantidadAseos: 0, totalNeto: 0, totalPagado: 0, totalPendiente: 0, totalSinEstado: 0 },
      );
      const fila: ResumenAseador = {
        aseadorId: id,
        nombre: resp.data.aseador.nombre,
        ...tot,
      };
      setResumenes((prev) => {
        const idx = prev.findIndex((r) => r.aseadorId === id);
        if (idx === -1) return [...prev, fila];
        const copy = [...prev];
        copy[idx] = fila;
        return copy;
      });
    },
    [buildRangeOpts],
  );

  // Suscripción WS: refresh cuando cambia el ledger del aseador
  // seleccionado (admin actuó desde otra pestaña, o se creó un gasto en
  // /finanzas-empresa). Y captura el recordatorio del cron lunes.
  useEffect(() => {
    if (!socket) return;
    const handler = (ev: MessageEvent) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg?.type === "ASEADOR_LEDGER_CHANGED") {
          if (vista === "por-aseador" && aseadorId && msg.aseadorId === aseadorId) {
            void loadDetalle(aseadorId);
          }
          // Vista global: en lugar de re-fetchear las N filas, solo
          // refrescamos la fila del aseador afectado. Una sola llamada
          // adicional, no impacta perf y mantiene los totales fresquitos.
          if (vista === "global" && Number.isFinite(msg.aseadorId)) {
            void refreshSingleResumen(Number(msg.aseadorId));
          }
        } else if (msg?.type === "ASEADOR_PAGOS_PENDIENTES_REVISION") {
          // Cron lunes — guardamos en estado para mostrar el Alert.
          setReminderLunes({
            from: msg.from,
            to: msg.to,
            aseadores: Array.isArray(msg.aseadores) ? msg.aseadores : [],
          });
        }
      } catch {
        // Ignorar mensajes mal formados.
      }
    };
    socket.addEventListener("message", handler);
    return () => socket.removeEventListener("message", handler);
  }, [socket, aseadorId, vista, loadDetalle, refreshSingleResumen]);

  // ─── Acciones admin: aprobar / denegar movimiento ───────────────────
  //
  // Round 2026-07-27 — los handlers reciben el MOVIMIENTO, no su id. Una
  // ocurrencia de flujo REGULAR (una suscripción o arriendo que AF le paga al
  // aseador todos los meses) no tiene fila propia: el regular es UNA fila de
  // `gastos` que vale por N meses, y su estado de pago se guarda por mes. Por
  // eso su transición va al endpoint de regulares con (gastoId, período).
  const transicionarMovimiento = useCallback(
    async (mov: MovimientoAseador, estado: MovimientoEstado) => {
      if (!aseadorId) return;
      let ok;
      if (mov.origen === "lavanderia_asignacion") {
        if (!mov.asignacionId || !mov.revision || estado === "denegado") return;
        ok = await cambiarEstadoAsignacionLavanderia(mov.asignacionId, mov.revision, estado);
      } else if (mov.esRegular && mov.periodo) {
        ok = await finanzasApi.marcarPagoRegularAseador(
          aseadorId,
          mov.id,
          mov.periodo,
          estado,
        );
      } else if (estado === "pagado") {
        ok = await finanzasApi.aprobarMovimientoAseador(aseadorId, mov.id);
      } else if (estado === "denegado") {
        ok = await finanzasApi.denegarMovimientoAseador(aseadorId, mov.id);
      } else {
        ok = await finanzasApi.revertirMovimientoAseador(aseadorId, mov.id);
      }
      if (ok) void loadDetalle(aseadorId);
    },
    [aseadorId, loadDetalle],
  );

  const handleApprove = useCallback(
    (mov: MovimientoAseador) => transicionarMovimiento(mov, "pagado"),
    [transicionarMovimiento],
  );

  const handleDeny = useCallback(
    (mov: MovimientoAseador) => transicionarMovimiento(mov, "denegado"),
    [transicionarMovimiento],
  );

  // Round 2026-04-30 quinquies — handlers para revertir/editar/pagar/desmarcar.
  const handleRevert = useCallback(
    (mov: MovimientoAseador) => transicionarMovimiento(mov, "pendiente"),
    [transicionarMovimiento],
  );

  // C5 (round 2026-06-20, D-D): borrar un movimiento. El backend solo permite
  // pendiente/denegado (un pagado se corrige con revertir); la UI ya restringe
  // el botón a esos estados.
  const handleDeleteMovimiento = useCallback(
    async (movimientoId: number) => {
      if (!aseadorId) return;
      const ok = await finanzasApi.borrarMovimientoAseador(
        aseadorId,
        movimientoId,
      );
      if (ok) void loadDetalle(aseadorId);
    },
    [aseadorId, loadDetalle],
  );

  const handlePagarAseo = useCallback(
    async (cleaningId: number) => {
      if (!aseadorId) return;
      const ok = await finanzasApi.pagarAseoIndividual(aseadorId, cleaningId);
      if (ok) void loadDetalle(aseadorId);
    },
    [aseadorId, loadDetalle],
  );

  const handleDesmarcarPagoAseo = useCallback(
    async (cleaningId: number) => {
      if (!aseadorId) return;
      const ok = await finanzasApi.desmarcarPagoAseo(aseadorId, cleaningId);
      if (ok) void loadDetalle(aseadorId);
    },
    [aseadorId, loadDetalle],
  );

  const handleEdit = useCallback((mov: MovimientoAseador) => {
    setEditingMov(mov);
  }, []);

  const handleEditSave = useCallback(
    async (patch: {
      monto?: number;
      concepto?: string;
      descripcion?: string;
      fecha?: string;
      signo?: 1 | -1;
    }) => {
      if (!aseadorId || !editingMov) return;
      const ok = await finanzasApi.editarMovimientoAseador(
        aseadorId,
        editingMov.id,
        patch,
      );
      if (ok) {
        setEditingMov(null);
        void loadDetalle(aseadorId);
      }
    },
    [aseadorId, editingMov, loadDetalle],
  );

  // Round 2026-04-30 quinquies — bulk approve por fila en la tabla global.
  // Click en el botón de la fila → aprueba todos los pendientes de ESE
  // aseador en el rango visible. Misma lógica que el botón "Aprobar todo
  // el rango" de la vista por-aseador, pero accesible sin drill-down.
  const [bulkRowLoading, setBulkRowLoading] = useState<Set<number>>(new Set());

  const handleApproveBulkRow = useCallback(
    async (rowAseadorId: number) => {
      setBulkRowLoading((prev) => new Set(prev).add(rowAseadorId));
      let visibleFrom: string;
      let visibleTo: string;
      if (rangeMode === "semana" && semana) {
        visibleFrom = semana.startOf("isoWeek").format("YYYY-MM-DD");
        visibleTo = semana.endOf("isoWeek").format("YYYY-MM-DD");
      } else if (rangeMode === "semana") {
        const lunesActual = ahoraChile().startOf("isoWeek");
        visibleFrom = lunesActual
          .subtract(weeks - 1, "week")
          .format("YYYY-MM-DD");
        visibleTo = lunesActual.endOf("isoWeek").format("YYYY-MM-DD");
      } else if (rangoMeses) {
        visibleFrom = rangoMeses[0].format("YYYY-MM-DD");
        visibleTo = rangoMeses[1].format("YYYY-MM-DD");
      } else {
        visibleFrom = ahoraChile()
          .subtract(months - 1, "month")
          .startOf("month")
          .format("YYYY-MM-DD");
        visibleTo = ahoraChile().endOf("month").format("YYYY-MM-DD");
      }
      const resp = await finanzasApi.aprobarPendientesBulkAseador(
        rowAseadorId,
        { rango: "rango_visible", from: visibleFrom, to: visibleTo },
      );
      setBulkRowLoading((prev) => {
        const next = new Set(prev);
        next.delete(rowAseadorId);
        return next;
      });
      if (resp) {
        // Refrescar la fila del aseador en la tabla global.
        void refreshSingleResumen(rowAseadorId);
      }
    },
    [rangeMode, semana, weeks, months, rangoMeses, refreshSingleResumen],
  );

  /**
   * Aprueba en bulk los pendientes del aseador seleccionado.
   * `rango='semana_anterior'`: lo que cubre el cron lunes (Lun-Dom anterior).
   * `rango='rango_visible'`: todo lo del rango N meses cargado en pantalla
   *   (más amplio, útil cuando hay backlog acumulado).
   */
  const handleApproveBulk = useCallback(
    async (rango: "semana_anterior" | "rango_visible") => {
      if (!aseadorId) return;
      setBulkLoading(true);
      // Round 2026-04-30 quater — el rango visible para "Aprobar todo el
      // rango" depende del modo activo. En semana mode usamos N semanas
      // ISO 8601, en meses usamos N meses calendario. Antes era siempre
      // meses, lo que generaba bug si admin estaba en vista semanal con
      // pendientes de hace 2 meses.
      let visibleFrom: string;
      let visibleTo: string;
      if (rangeMode === "semana" && semana) {
        visibleFrom = semana.startOf("isoWeek").format("YYYY-MM-DD");
        visibleTo = semana.endOf("isoWeek").format("YYYY-MM-DD");
      } else if (rangeMode === "semana") {
        const lunesActual = ahoraChile().startOf("isoWeek");
        visibleFrom = lunesActual
          .subtract(weeks - 1, "week")
          .format("YYYY-MM-DD");
        visibleTo = lunesActual.endOf("isoWeek").format("YYYY-MM-DD");
      } else if (rangoMeses) {
        visibleFrom = rangoMeses[0].format("YYYY-MM-DD");
        visibleTo = rangoMeses[1].format("YYYY-MM-DD");
      } else {
        visibleFrom = ahoraChile()
          .subtract(months - 1, "month")
          .startOf("month")
          .format("YYYY-MM-DD");
        visibleTo = ahoraChile().endOf("month").format("YYYY-MM-DD");
      }
      const body =
        rango === "semana_anterior"
          ? { rango }
          : { rango, from: visibleFrom, to: visibleTo };
      const resp = await finanzasApi.aprobarPendientesBulkAseador(
        aseadorId,
        body,
      );
      setBulkLoading(false);
      if (resp) {
        void loadDetalle(aseadorId);
        // Si el admin acaba de resolver el recordatorio del lunes, lo
        // ocultamos. El aseador siguiente todavía puede tener pendientes,
        // pero el cron ya no se reactiva hasta el próximo lunes.
        if (rango === "semana_anterior") {
          setReminderLunes((prev) => {
            if (!prev) return prev;
            const filtered = prev.aseadores.filter(
              (a) => a.aseadorId !== aseadorId,
            );
            return filtered.length > 0
              ? { ...prev, aseadores: filtered }
              : null;
          });
        }
      }
    },
    [aseadorId, rangeMode, months, weeks, semana, rangoMeses, loadDetalle],
  );

  /**
   * loadGlobal: vista global con N filas. Round 2026-04-30 quater — ahora
   * respeta el rango (meses/semana/semana específica) del toolbar igual
   * que la vista por-aseador. Antes solo soportaba meses, lo que rompía
   * la coherencia: admin elegía "Semana 27-04→03-05" en el toolbar y la
   * tabla global mostraba totales de los últimos N meses (totalmente
   * distinto). Ahora ambos modos usan `buildRangeOpts()` para que los
   * números coincidan entre vistas.
   */
  // Cargar la vista global haciendo N llamadas en paralelo.
  // Cuándo: al entrar a la pestaña global o al cambiar el rango. Cancelable
  // si el componente se desmonta o si cambia la pestaña antes de terminar.
  const loadGlobal = useCallback(
    async (list: Cleaner[]) => {
      setLoadingGlobal(true);
      const opts = buildRangeOpts();
      // En paralelo: los N aseadores reales + el caso "Sin aseador" (aseos
      // abiertos del rango). La fila especial va al final de la tabla.
      const [results, sinResp] = await Promise.all([
        Promise.allSettled(
          list.map((a) =>
            finanzasApi
              .getFinanzasAseadorById(a.id, opts)
              .then((resp) => ({ aseador: a, resp })),
          ),
        ),
        finanzasApi.getFinanzasAseadorSinAsignar(opts),
      ]);

      const filas: ResumenAseador[] = results.map((r, i) => {
        const aseador = list[i];
        if (r.status === "rejected" || !r.value.resp) {
          return {
            aseadorId: aseador.id,
            nombre: aseador.nombre,
            avatar: aseador.avatar ?? null,
            avatarUrl: aseador.avatar_url ?? null,
            cantidadAseos: 0,
            totalNeto: 0,
            totalPagado: 0,
            totalPendiente: 0,
            error: true,
          };
        }
        const { resp } = r.value;
        // Ledger COMPLETO (aseos + movimientos extra no-denegados): usamos
        // totalGeneral* para que la tabla global reconcilie con el detalle
        // por-aseador (FinanzasAseadorView usa totalGeneralNeto) y con el banner
        // "Total general = Σ aseos.neto + Σ movimientos". Antes sumaba solo
        // totalNeto (aseos), divergiendo del drill-down y dejando "Pendiente" en
        // 0 cuando los únicos pendientes eran movimientos. (Audit 2026-06-21.)
        const tot = resp.data.meses.reduce(
          (acc, mes) => ({
            cantidadAseos: acc.cantidadAseos + mes.cantidadAseos,
            totalNeto: acc.totalNeto + mes.totalGeneralNeto,
            totalPagado: acc.totalPagado + mes.totalGeneralPagado,
            totalPendiente: acc.totalPendiente + mes.totalGeneralPendiente,
            totalSinEstado: acc.totalSinEstado + (mes.totalMovimientosSinEstado || 0),
          }),
          { cantidadAseos: 0, totalNeto: 0, totalPagado: 0, totalPendiente: 0, totalSinEstado: 0 },
        );
        return {
          aseadorId: aseador.id,
          nombre: resp.data.aseador.nombre || aseador.nombre,
          avatar: aseador.avatar ?? null,
          avatarUrl: aseador.avatar_url ?? null,
          ...tot,
        };
      });

      // Orden por neto descendente — el aseador con más volumen va arriba.
      filas.sort((a, b) => b.totalNeto - a.totalNeto);

      // Fila especial "Sin aseador designado" al FINAL (solo si hay aseos
      // abiertos en el rango). Read-only: no entra en los KPIs de arriba
      // (`totalesGlobales` la excluye) ni ofrece acción de aprobar.
      if (sinResp) {
        const totSin = sinResp.data.meses.reduce(
          (acc, mes) => ({
            cantidadAseos: acc.cantidadAseos + mes.cantidadAseos,
            totalNeto: acc.totalNeto + mes.totalGeneralNeto,
            totalPagado: acc.totalPagado + mes.totalGeneralPagado,
            totalPendiente: acc.totalPendiente + mes.totalGeneralPendiente,
            totalSinEstado: acc.totalSinEstado + (mes.totalMovimientosSinEstado || 0),
          }),
          { cantidadAseos: 0, totalNeto: 0, totalPagado: 0, totalPendiente: 0, totalSinEstado: 0 },
        );
        if (totSin.cantidadAseos > 0) {
          filas.push({
            aseadorId: SIN_ASEADOR,
            nombre: "Sin aseador designado",
            ...totSin,
            sinAseador: true,
          });
        }
      }

      setResumenes(filas);
      setLoadingGlobal(false);
    },
    [buildRangeOpts],
  );

  useEffect(() => {
    if (vista === "global" && aseadores.length > 0) {
      void loadGlobal(aseadores);
    }
    // El effect re-corre cuando cualquier dimensión del rango cambia
    // (rangeMode, months, weeks, semana, rangoMeses) gracias a la closure de
    // `buildRangeOpts` que loadGlobal cierra como dependencia.
  }, [vista, rangeMode, months, weeks, semana, rangoMeses, aseadores, loadGlobal]);

  // ─── Totales globales para mostrar en KPIs arriba de la tabla. ─────
  const totalesGlobales = useMemo(() => {
    // La fila "Sin aseador" NO entra a los KPIs (no es plata de ningún
    // aseador; es trabajo sin asignar, ya contado en la utilidad AF).
    return resumenes
      .filter((r) => !r.sinAseador)
      .reduce(
      (acc, r) => ({
        aseadores: acc.aseadores + 1,
        cantidadAseos: acc.cantidadAseos + r.cantidadAseos,
        totalNeto: acc.totalNeto + r.totalNeto,
        totalPagado: acc.totalPagado + r.totalPagado,
        totalPendiente: acc.totalPendiente + r.totalPendiente,
        totalSinEstado: acc.totalSinEstado + (r.totalSinEstado || 0),
      }),
      {
        aseadores: 0,
        cantidadAseos: 0,
        totalNeto: 0,
        totalPagado: 0,
        totalPendiente: 0,
        totalSinEstado: 0,
      },
    );
  }, [resumenes]);

  // Filtro de búsqueda en la tabla global. fuzzyIncludes pliega tildes
  // ("jose" encuentra "José"). Aud. 2026-07-13.
  const resumenesFiltrados = useMemo(() => {
    const q = busquedaGlobal.trim();
    if (!q) return resumenes;
    return resumenes.filter((r) => fuzzyIncludes(r.nombre, q));
  }, [resumenes, busquedaGlobal]);

  const columns: ColumnsType<ResumenAseador> = [
    {
      title: "Aseador",
      dataIndex: "nombre",
      key: "nombre",
      render: (nombre: string, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {row.sinAseador ? (
            // Fila especial "Sin aseador": ícono neutro (no es una persona real).
            <span
              className="user-avatar"
              style={{
                width: 32,
                height: 32,
                minWidth: 32,
                background: "#f1f5f9",
                color: "#64748b",
                boxShadow: "0 0 0 1.5px #e2e8f0",
              }}
            >
              <UserOutlined />
            </span>
          ) : (
            // Avatar REAL del aseador: foto subida > avatar generado propio >
            // Multiavatar del nombre. Antes era siempre multiavatar(nombre).
            <UserAvatar
              nombre={nombre}
              avatarSeed={row.avatar}
              avatarUrl={row.avatarUrl}
              size={32}
            />
          )}
          <span>
            {nombre}
            {row.sinAseador ? (
              <Tooltip title="Aseos con id_usuario NULL (no asignados). Solo lectura: no son pagables hasta que un aseador los tome.">
                <Tag color="default" style={{ marginLeft: 8 }}>
                  Aseos abiertos
                </Tag>
              </Tooltip>
            ) : null}
            {row.error ? (
              <Tooltip title="No se pudo cargar el detalle de este aseador. Vuelve a intentar.">
                <Tag color="red" style={{ marginLeft: 8 }}>
                  Error
                </Tag>
              </Tooltip>
            ) : null}
          </span>
        </div>
      ),
      sorter: (a, b) => a.nombre.localeCompare(b.nombre),
    },
    {
      title: "Aseos",
      dataIndex: "cantidadAseos",
      key: "cantidadAseos",
      width: 100,
      align: "right",
      sorter: (a, b) => a.cantidadAseos - b.cantidadAseos,
      render: (v: number) => (
        <Tag color={v > 0 ? "blue" : "default"}>{v}</Tag>
      ),
    },
    {
      title: "Neto del rango",
      dataIndex: "totalNeto",
      key: "totalNeto",
      width: 160,
      align: "right",
      sorter: (a, b) => a.totalNeto - b.totalNeto,
      defaultSortOrder: "descend",
      render: (v: number) => (
        <strong className="fa-stat-neto">{fmtClp(v)}</strong>
      ),
    },
    {
      title: "Ya pagado",
      dataIndex: "totalPagado",
      key: "totalPagado",
      width: 140,
      align: "right",
      sorter: (a, b) => a.totalPagado - b.totalPagado,
      render: (v: number) => (
        <span className="fa-stat-pagado">{fmtClp(v)}</span>
      ),
    },
    {
      title: "Pendiente",
      dataIndex: "totalPendiente",
      key: "totalPendiente",
      width: 140,
      align: "right",
      sorter: (a, b) => a.totalPendiente - b.totalPendiente,
      render: (v: number) => (
        <span className="fa-stat-pendiente">{fmtClp(v)}</span>
      ),
    },
    {
      // Round 2026-04-30 quinquies — bulk approve por fila. Aprueba todos
      // los movimientos pendientes de ESE aseador en el rango visible. La
      // fila se filtra al click para que admin no se vaya al detalle si
      // solo quiere "OK, aprobar todo lo del Juan en esta semana".
      title: "Sin estado",
      key: "totalSinEstado",
      dataIndex: "totalSinEstado",
      align: "right",
      render: (n: number | undefined) => <Tooltip title="Incluido en el neto; aún no se indicó si está pendiente o pagado.">{fmtClp(n || 0)}</Tooltip>,
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 180,
      render: (_: unknown, row: ResumenAseador) => {
        // La fila "Sin aseador" no ofrece aprobar/pagar (pago fantasma).
        if (row.sinAseador) {
          return (
            <Tooltip title="Aseos sin aseador designado: solo lectura. Asigna un aseador desde /aseos o /calendario para poder pagarlos.">
              <Text type="secondary" style={{ fontSize: 12 }}>
                Solo lectura
              </Text>
            </Tooltip>
          );
        }
        return (
          <Space size={4} onClick={(e) => e.stopPropagation()}>
            <Popconfirm
              title="Aprobar movimientos generales (rango visible)"
              description={`Aprueba los movimientos generales de ${row.nombre}. Los pagos de lavandería se aprueban individualmente.`}
              okText="Aprobar"
              cancelText="Cancelar"
              onConfirm={() => handleApproveBulkRow(row.aseadorId)}
              disabled={row.totalPendiente <= 0 || !!row.error}
            >
              <Button
                size="small"
                type="primary"
                icon={<CheckSquareOutlined />}
                loading={bulkRowLoading.has(row.aseadorId)}
                disabled={row.totalPendiente <= 0 || !!row.error}
              >
                Aprobar todo
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Layout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}>
      <div className="fa-page faa-page">
        <PageHeader
          title="Finanzas del aseador"
          helpText="Vista admin/superadmin: examina las finanzas que ve cada aseador, o un panel global con todos. Los datos son los mismos que el aseador ve en su propio panel — la fórmula vive en el backend para garantizar paridad."
        />

        <ModuleInfoBanner />

        {/* Recordatorio lunes (Fase 3): el cron `aseadorPagosCron`
            disparó un WS con resumen de pendientes por aseador. Mientras
            la pestaña está abierta, mostramos un Alert persistente con
            la lista clickeable. Click en un aseador → drill-down al
            detalle para que admin revise y apruebe (individual o bulk). */}
        {reminderLunes && reminderLunes.aseadores.length > 0 && (
          <Alert
            type="warning"
            showIcon
            closable
            onClose={() => setReminderLunes(null)}
            style={{ marginBottom: 12 }}
            message={
              <strong>
                Lunes — pendientes de aprobación de la semana anterior (
                {dayjs(reminderLunes.from).format("DD-MM")} →{" "}
                {dayjs(reminderLunes.to).format("DD-MM")})
              </strong>
            }
            description={
              <div>
                <ul style={{ margin: "8px 0", paddingLeft: 20 }}>
                  {reminderLunes.aseadores.map((a) => (
                    <li key={a.aseadorId}>
                      <a
                        onClick={(e) => {
                          e.preventDefault();
                          setVista("por-aseador");
                          setAseadorId(a.aseadorId);
                        }}
                      >
                        <strong>{a.nombre}</strong>
                      </a>{" "}
                      — <Tag color="gold">{a.count} pendientes</Tag>
                      <span
                        style={{
                          color: a.monto >= 0 ? "#16a34a" : "#dc2626",
                        }}
                      >
                        {a.monto >= 0 ? "+" : ""}
                        ${Math.round(a.monto).toLocaleString("es-CL")} neto
                      </span>
                    </li>
                  ))}
                </ul>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Selecciona un aseador y revisa cada movimiento. Si todo
                  está OK, usa la aprobación general; los pagos de lavandería
                  se revisan y aprueban individualmente.
                </Text>
              </div>
            }
          />
        )}

        <div className="faa-toolbar">
          <Segmented
            value={vista}
            onChange={(v) => setVista(v as "por-aseador" | "global")}
            options={[
              { label: "Por aseador", value: "por-aseador" },
              { label: "Global", value: "global" },
            ]}
          />
          {/* Toggle modo de rango. Round 2026-04-30 quater — la vista
              global ahora SÍ soporta semana (antes obligaba meses). El
              admin entra al panel viendo la semana actual con totales
              agregados de todos los aseadores en esa semana, click en
              fila → drill-down detalle. */}
          <Segmented
            value={rangeMode}
            onChange={(v) => setRangeMode(v as "meses" | "semana")}
            options={[
              { label: "Meses", value: "meses" },
              { label: "Semana", value: "semana" },
            ]}
          />
          {rangeMode === "meses" ? (
            <>
              <Segmented
                value={months}
                onChange={(v) => setMonths(Number(v))}
                disabled={!!rangoMeses}
                options={[
                  { label: "3 meses", value: 3 },
                  { label: "6 meses", value: 6 },
                  { label: "12 meses", value: 12 },
                ]}
              />
              {/* Rango de fechas puntual (override del segmento N meses).
                  Muestra UN solo total del período elegido. Round 2026-07-12. */}
              <Tooltip title="Opcional: ver un rango de fechas puntual (un solo total del período elegido). Si lo dejas vacío, se respeta el selector de N meses.">
                <RangePicker
                  value={rangoMeses}
                  onChange={(v) =>
                    setRangoMeses(v && v[0] && v[1] ? [v[0], v[1]] : null)
                  }
                  allowClear
                  format="DD-MM-YYYY"
                  placeholder={["Desde", "Hasta"]}
                  style={{ minWidth: 260 }}
                />
              </Tooltip>
            </>
          ) : (
            <>
              {/* Cantidad de semanas a mostrar como paneles. Cada panel
                  cubre Lun-Dom ISO 8601. Default 4 — cubre el típico
                  "mes laboral" del aseador. */}
              <Segmented
                value={weeks}
                onChange={(v) => setWeeks(Number(v))}
                disabled={!!semana}
                options={[
                  { label: "4 sem.", value: 4 },
                  { label: "8 sem.", value: 8 },
                  { label: "12 sem.", value: 12 },
                ]}
              />
              {/* Picker opcional para saltar a UNA semana específica
                  (override del segmented). Útil para auditar pagos viejos
                  sin tener que buscar dentro de 12 paneles. */}
              <Tooltip
                title={
                  <span>
                    <b>Opcional</b>: salta a una semana puntual (Lun-Dom).
                    El pago canónico cae el <b>lunes siguiente</b> al
                    domingo elegido (ej. semana 1-7 → pago el día 8).
                    Si lo dejás vacío, se respeta el selector de "N semanas".
                  </span>
                }
              >
                <DatePicker
                  picker="week"
                  value={semana}
                  onChange={(v) => setSemana(v)}
                  placeholder="Ir a semana específica"
                  allowClear
                  style={{ minWidth: 220 }}
                  format={(v) =>
                    `Sem. ${v.startOf("isoWeek").format("DD-MM")} → ${v
                      .endOf("isoWeek")
                      .format("DD-MM")}`
                  }
                />
              </Tooltip>
            </>
          )}
        </div>

        {vista === "por-aseador" ? (
          <>
            <div className="faa-selector">
              <Text strong style={{ marginRight: 8 }}>
                Aseador:
              </Text>
              <Select
                showSearch
                allowClear
                placeholder="Selecciona un aseador..."
                style={{ minWidth: 320 }}
                loading={loadingAseadores}
                value={aseadorId ?? undefined}
                onChange={(v: number | undefined) => setAseadorId(v ?? null)}
                optionFilterProp="label"
                options={[
                  // Caso especial primero: aseos sin aseador designado
                  // (id_usuario IS NULL). Solo lectura, sin acciones de pago.
                  {
                    value: SIN_ASEADOR,
                    label: "— Sin aseador designado (aseos abiertos) —",
                  },
                  ...aseadores.map((a) => ({
                    value: a.id,
                    label: a.nombre,
                  })),
                ]}
              />
              {aseadorNombre ? (
                <Text type="secondary" style={{ marginLeft: 12 }}>
                  Viendo: <strong>{aseadorNombre}</strong>
                </Text>
              ) : null}
            </div>

            {!aseadorId ? (
              <Empty
                description="Selecciona un aseador en la lista de arriba para ver su panel."
                style={{ padding: "40px 0" }}
              />
            ) : aseadorId === SIN_ASEADOR ? (
              // Caso "Sin aseador designado": SOLO LECTURA. No pasamos ningún
              // callback de pago/aprobación → la vista no pinta esas acciones.
              // El prop `sinAseador` muestra un banner aclaratorio.
              <FinanzasAseadorView
                meses={meses}
                months={months}
                loading={loadingDetalle}
                error={errorDetalle}
                onChangeMonths={setMonths}
                onReload={() => void loadDetalle(SIN_ASEADOR)}
                hideProximoPago
                hideRangeSelector
                sinAseador
              />
            ) : (
              <FinanzasAseadorView
                meses={meses}
                months={months}
                loading={loadingDetalle}
                error={errorDetalle}
                onChangeMonths={setMonths}
                onReload={() => void loadDetalle(aseadorId)}
                hideProximoPago
                hideRangeSelector
                onApproveMovimiento={handleApprove}
                onDenyMovimiento={handleDeny}
                onRevertMovimiento={handleRevert}
                onEditMovimiento={handleEdit}
                onDeleteMovimiento={handleDeleteMovimiento}
                onPagarAseo={handlePagarAseo}
                onDesmarcarPagoAseo={handleDesmarcarPagoAseo}
                toolbarExtra={
                  <>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setRegistrarModalOpen(true)}
                    >
                      Registrar movimiento
                    </Button>
                    {/* Bulk Lun-Dom anterior — alineado con el pago de
                        aseos del lunes. Es el botón canónico del flujo
                        del cron lunes. */}
                    <Popconfirm
                      title="Aprobar movimientos generales — semana anterior"
                      description="Aprueba movimientos generales pendientes de la semana pasada. Los pagos de lavandería se aprueban individualmente."
                      okText="Aprobar"
                      cancelText="Cancelar"
                      onConfirm={() => handleApproveBulk("semana_anterior")}
                    >
                      <Button
                        icon={<CheckSquareOutlined />}
                        loading={bulkLoading}
                      >
                        Aprobar generales (semana anterior)
                      </Button>
                    </Popconfirm>
                    {/* Bulk del rango visible — útil cuando hay backlog. */}
                    <Popconfirm
                      title="Aprobar movimientos generales — rango visible"
                      description={`Aprueba movimientos generales del rango cargado (últimos ${months} ${months === 1 ? "mes" : "meses"}). Los pagos de lavandería se aprueban individualmente.`}
                      okText="Aprobar"
                      cancelText="Cancelar"
                      onConfirm={() => handleApproveBulk("rango_visible")}
                    >
                      <Button loading={bulkLoading}>
                        Aprobar todo el rango
                      </Button>
                    </Popconfirm>
                  </>
                }
              />
            )}
          </>
        ) : (
          <>
            <div className="faa-global-kpis">
              <div className="fa-rango-card">
                <div>
                  <div className="fa-rango-label">Aseadores activos</div>
                  <strong className="fa-rango-value">
                    {totalesGlobales.aseadores}
                  </strong>
                </div>
              </div>
              <div className="fa-rango-card">
                <div>
                  <div className="fa-rango-label">Aseos del rango</div>
                  <strong className="fa-rango-value">
                    {totalesGlobales.cantidadAseos}
                  </strong>
                </div>
              </div>
              <div className="fa-rango-card">
                <div>
                  <div className="fa-rango-label">Neto total</div>
                  <strong className="fa-rango-value fa-stat-neto">
                    {fmtClp(totalesGlobales.totalNeto)}
                  </strong>
                </div>
              </div>
              <div className="fa-rango-card">
                <div>
                  <div className="fa-rango-label">Ya pagado</div>
                  <strong className="fa-rango-value fa-stat-pagado">
                    {fmtClp(totalesGlobales.totalPagado)}
                  </strong>
                </div>
              </div>
              <div className="fa-rango-card">
                <div>
                  <div className="fa-rango-label">Pendiente</div>
                  <strong className="fa-rango-value fa-stat-pendiente">
                    {fmtClp(totalesGlobales.totalPendiente)}
                  </strong>
                </div>
              </div>
              <div className="fa-rango-card"><div><div className="fa-rango-label">Sin estado</div><strong className="fa-rango-value">{fmtClp(totalesGlobales.totalSinEstado)}</strong></div></div>
            </div>

            <div className="faa-search">
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Buscar aseador por nombre..."
                value={busquedaGlobal}
                onChange={(e) => setBusquedaGlobal(e.target.value)}
                style={{ maxWidth: 320 }}
              />
              <Title level={5} style={{ margin: 0, marginLeft: "auto" }}>
                {resumenesFiltrados.length}{" "}
                {resumenesFiltrados.length === 1 ? "aseador" : "aseadores"}
              </Title>
            </div>

            {loadingGlobal ? (
              <div className="fa-loading">
                <Spin size="large" tip="Calculando totales..." />
              </div>
            ) : (
              <Table<ResumenAseador>
                rowKey="aseadorId"
                dataSource={resumenesFiltrados}
                columns={columns}
                pagination={paginacionCliente({ porDefecto: 20, etiqueta: "aseador" })}
                onRow={(row) => ({
                  // Click → ir a la vista por-aseador con este aseador
                  // pre-seleccionado. Atajo de drill-down sin perder rango.
                  onClick: () => {
                    setAseadorId(row.aseadorId);
                    setVista("por-aseador");
                  },
                  style: { cursor: "pointer" },
                })}
                locale={{ emptyText: "Sin aseadores que coincidan." }}
              />
            )}
          </>
        )}

        {/* Modal liviano para registrar adelanto/bono/multa/reembolso/etc.
            Solo abre desde el botón "Registrar movimiento" en la toolbar
            de "Por aseador". El backend deja el row en estado='pendiente'
            hasta que admin apruebe. */}
        <RegistrarMovimientoModal
          open={registrarModalOpen}
          aseadorId={aseadorId}
          aseadorNombre={aseadorNombre}
          onClose={() => setRegistrarModalOpen(false)}
          onCreated={() => {
            if (aseadorId) void loadDetalle(aseadorId);
          }}
        />

        {/* Modal de edición — round 2026-04-30 quinquies. Inline (no archivo
            separado) porque es un form chico de 4 campos. Si crece más en
            el futuro, refactor a su propio componente. */}
        <EditarMovimientoModal
          movimiento={editingMov}
          onClose={() => setEditingMov(null)}
          onSave={handleEditSave}
        />
      </div>
    </Layout>
  );
}

/**
 * Modal para editar un movimiento existente. Permite cambiar monto,
 * concepto (texto libre), descripción y fecha. El backend regresa el row
 * a `pendiente` automáticamente si estaba en `pagado`/`denegado` para
 * forzar re-aprobación tras la edición.
 */
function EditarMovimientoModal({
  movimiento,
  onClose,
  onSave,
}: {
  movimiento: MovimientoAseador | null;
  onClose: () => void;
  onSave: (patch: {
    monto?: number;
    concepto?: string;
    descripcion?: string;
    fecha?: string;
    signo?: 1 | -1;
  }) => Promise<void>;
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!movimiento) return;
    form.setFieldsValue({
      monto: movimiento.monto,
      concepto: movimiento.conceptoCodigo || "",
      descripcion: movimiento.descripcion || "",
      fecha: movimiento.fecha ? dayjs(movimiento.fecha) : null,
      // C4 (D-C): prefill de la dirección actual para poder corregirla.
      signo: movimiento.signo,
    });
  }, [movimiento, form]);

  const handleOk = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const v = await form.validateFields();
      await onSave({
        monto: v.monto,
        concepto: v.concepto?.trim() || undefined,
        descripcion: v.descripcion?.trim() || undefined,
        fecha: v.fecha ? v.fecha.format("YYYY-MM-DD") : undefined,
        // Solo mandamos signo si cambió, para no forzar re-derivación inútil.
        signo: v.signo !== movimiento?.signo ? (v.signo as 1 | -1) : undefined,
      });
    } catch {
      // Form validation rechazada o request fallido.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Editar movimiento"
      open={!!movimiento}
      onCancel={onClose}
      onOk={handleOk}
      okText="Guardar"
      cancelText="Cancelar"
      confirmLoading={saving}
      destroyOnClose
    >
      {movimiento && movimiento.estado !== "pendiente" && (
        <Alert
          type="warning"
          showIcon
          message="Editar volverá el movimiento a 'pendiente'"
          description="Cualquier edición material requiere re-aprobación. Después de guardar, el movimiento queda en pendiente y debes aprobarlo nuevamente."
          style={{ marginBottom: 12 }}
        />
      )}
      <Form form={form} layout="vertical">
        <Form.Item
          name="monto"
          label="Monto"
          rules={[
            { required: true, message: "Ingresa un monto" },
            {
              type: "number",
              min: 0,
              message: "El monto debe ser ≥ 0",
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            step={1000}
            formatter={(v) =>
              v === undefined || v === null
                ? ""
                : `$ ${Number(v).toLocaleString("es-CL")}`
            }
            parser={((v: string | undefined) =>
              Number((v || "").replace(/[^0-9]/g, "")) || 0) as any}
          />
        </Form.Item>
        {/* C4 (round 2026-06-20, D-C): corregir la dirección de un movimiento
            mal ingresado sin tener que borrarlo y recrearlo. El backend
            re-deriva pagado_por/pagado_a conservando la contraparte. */}
        <Form.Item
          name="signo"
          label="Dirección del flujo"
          rules={[{ required: true, message: "Indica la dirección" }]}
          tooltip="Aseador recibe = el dinero entra a su cuenta (ingreso). Aseador paga = sale de su cuenta (multa, devolución a AF). Cambiarlo corrige una dirección mal ingresada."
        >
          <Radio.Group>
            <Radio value={1}>Aseador recibe (+)</Radio>
            <Radio value={-1}>Aseador paga (−)</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="concepto" label="Concepto">
          <Input
            placeholder="adelanto, bono_extra, multa, reembolso_gasto, …"
            maxLength={60}
          />
        </Form.Item>
        <Form.Item name="fecha" label="Fecha">
          <DatePicker
            format="DD-MM-YYYY"
            style={{ width: "100%" }}
            allowClear={false}
          />
        </Form.Item>
        <Form.Item name="descripcion" label="Descripción">
          <Input.TextArea rows={3} maxLength={1000} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}
